import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { listReleases } from '../services/PublishTargetsService';

/**
 * The History tab's "Published" section: what the project published beyond Console (GET /publish/releases, newest
 * first), recorded by the pod after a Git or GitOps push and by the gateway after an image build: the kind, where, when,
 * who, each image's digest (copy the full one), the chart and the merge request. Nothing at all when there is none or
 * the sandbox is older (404).
 */
const KIND_LABELS = { registry: 'Registry', git: 'Git', gitops: 'GitOps', chart: 'Chart' };
const SHOWN = 10;
const shortDigest = (digest) => (typeof digest === 'string' ? digest.replace(/^sha256:/, '').slice(0, 12) : '');

function CopyDigest({ digest, name }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(digest); setCopied(true); } catch { setCopied(false); }
  };
  return (
    <button aria-label={`Copy the digest of ${name}`} className="history-published-copy" onClick={copy} title={digest} type="button">
      {copied ? 'Copied' : shortDigest(digest)}
    </button>
  );
}
CopyDigest.propTypes = { digest: PropTypes.string.isRequired, name: PropTypes.string.isRequired };

/** `by` as the server records it: a person `{ name, email }`, or an actor id the project's people resolve. */
function whoOf(by, actorOf) {
  if (!by) { return ''; }
  if (typeof by === 'object') { return by.name || by.email || by.sub || ''; }
  const id = String(by);
  return actorOf ? actorOf(id.includes(':') ? id : `user:${id}`).name : id;
}

export default function PublishedReleases({ projectId, actorOf, formatTime }) {
  const [releases, setReleases] = useState([]);
  const [all, setAll] = useState(false);

  useEffect(() => {
    let live = true;
    if (!projectId) { return undefined; }
    listReleases(projectId)
      .then((answer) => { if (live) { setReleases(Array.isArray(answer?.releases) ? answer.releases : []); } })
      .catch(() => { if (live) { setReleases([]); } });
    return () => { live = false; };
  }, [projectId]);

  if (!releases.length) { return null; }
  const shown = all ? releases : releases.slice(0, SHOWN);
  return (
    <section aria-label="Published" className="history-published" data-testid="history-published">
      <h3 className="history-published-title">Published</h3>
      <ul className="history-published-list">
        {shown.map((release, index) => (
          <li className="history-published-item" key={release.id || `${release.at}-${index}`}>
            <div className="history-published-head">
              <span className="history-published-kind">{KIND_LABELS[release.kind] || release.kind}</span>
              {release.url
                ? <a className="history-published-target" href={release.url} rel="noopener noreferrer" target="_blank">{release.target || release.url}</a>
                : <span className="history-published-target">{release.target}</span>}
              {release.branch && <span className="history-published-branch">{release.branch}</span>}
            </div>
            <div className="history-commit-meta">
              {whoOf(release.by, actorOf) && <span>{whoOf(release.by, actorOf)}</span>}
              {release.at && <span title={release.at}>{formatTime ? formatTime(release.at) : release.at}</span>}
              {release.mergeRequestUrl && <a href={release.mergeRequestUrl} rel="noopener noreferrer" target="_blank">Merge request</a>}
            </div>
            {release.images?.length > 0 && (
              <ul className="history-published-images">
                {release.images.map((image) => (
                  <li key={`${image.name}-${image.digest}`}>
                    <span className="history-published-image">{image.name}</span>
                    {image.digest && <CopyDigest digest={image.digest} name={image.service || image.name} />}
                  </li>
                ))}
              </ul>
            )}
            {release.chart?.ref && (
              <div className="history-published-images">
                <span className="history-published-image">Chart {release.chart.ref}{release.chart.version ? ` ${release.chart.version}` : ''}</span>
              </div>
            )}
          </li>
        ))}
      </ul>
      {releases.length > SHOWN && (
        <button className="history-published-more" onClick={() => setAll(!all)} type="button">
          {all ? 'Show fewer' : `Show all ${releases.length}`}
        </button>
      )}
    </section>
  );
}

PublishedReleases.propTypes = {
  actorOf: PropTypes.func,
  formatTime: PropTypes.func,
  projectId: PropTypes.string,
};
